import { useMemo, useState } from 'react'

import { useBlocker } from '@/shared/hooks/useBlocker'
import {
  effectiveFilter,
  groupByStatus,
  visibleFilterOptions
} from '@/shared/lib/list-view'
import { isRecent } from '@/shared/lib/recency'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import { useQuizzesStore } from '@/shared/store/useQuizzesStore'
import type { BlockCondition } from '@/types/blocker'
import type { Quiz, QuizProgress } from '@/types/quiz'

import {
  QUIZ_SORT_PRIORITY,
  QUIZ_STATUS_LABELS,
  type QuizFilterId,
  type QuizUiStatus
} from '../constants/status'
import { QUIZ_FILTER_OPTIONS } from '../lib/quizFilterOptions'
import { selectFeaturedQuiz } from '../lib/selectFeaturedQuiz'
import {
  getQuizProgressPercent,
  getQuizUiStatus,
  quizCanRetry
} from '../lib/quizUiStatus'

export type QuizListItem = Quiz & {
  _blockCondition: BlockCondition
  _progress?: QuizProgress
  canRetry: boolean
  uiStatus: QuizUiStatus
  /** Eixo independente de `uiStatus` — ver `shared/lib/recency`. */
  isNew: boolean
  progressPercent: number
}

/* Exportado para teste — ver nota em `useMissionsListView`. */
export const matchesFilter = (item: QuizListItem, filter: QuizFilterId): boolean => {
  if (filter === 'all') return true
  /* `new` é recência, não status — ver `shared/lib/recency`. */
  if (filter === 'new') return item.isNew
  return item.uiStatus === filter
}

export const useQuizzesListView = () => {
  const { quizzes: rawQuizzes } = useQuizzesStore()
  const { quizProgress } = useUserProgress()
  const { checkBlockCondition } = useBlocker()
  const { version } = useGamificationDataStore(s => s.getActiveProductData())
  const [rawFilter, setFilter] = useState<QuizFilterId>('all')

  const items = useMemo((): QuizListItem[] => {
    /* Um único instante para a lista inteira — ver `shared/lib/recency`. */
    const now = Date.now()

    return rawQuizzes.map(quiz => {
      const progress = quizProgress.find(
        (entry: QuizProgress) => entry.quiz_id === quiz.id
      )
      const canRetry = quizCanRetry(quiz, progress)
      const withMeta = {
        ...quiz,
        _progress: progress,
        canRetry
      }
      const blockResult = checkBlockCondition(quiz._blockCondition)
      const uiStatus = getQuizUiStatus(withMeta, blockResult)

      return {
        ...withMeta,
        uiStatus,
        isNew: isRecent(quiz.created_at, now),
        progressPercent: getQuizProgressPercent(withMeta, uiStatus)
      }
    })
  }, [rawQuizzes, quizProgress, checkBlockCondition])

  const visibleFilters = useMemo(
    () =>
      visibleFilterOptions(QUIZ_FILTER_OPTIONS, id =>
        items.some(item => matchesFilter(item, id))
      ),
    [items]
  )

  const filter = effectiveFilter(rawFilter, visibleFilters)

  const sortedItems = useMemo(() => {
    const filtered = items.filter(item => matchesFilter(item, filter))

    return [...filtered].sort((a, b) => {
      const priority =
        QUIZ_SORT_PRIORITY[a.uiStatus] - QUIZ_SORT_PRIORITY[b.uiStatus]

      if (priority !== 0) return priority

      return (a.position ?? 0) - (b.position ?? 0)
    })
  }, [items, filter])

  const featuredQuiz = useMemo(() => selectFeaturedQuiz(items), [items])

  const globalProgress = useMemo(() => {
    const total = items.length
    const completed = items.filter(q => q.uiStatus === 'success').length
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0

    const incomplete = items.filter(
      q => q.uiStatus === 'available' || q.uiStatus === 'progress'
    )

    const availablePoints = incomplete.reduce(
      (sum, q) => sum + (q.point_reward ?? 0),
      0
    )
    const availableCoins = incomplete.reduce(
      (sum, q) => sum + (q.coin_reward ?? 0),
      0
    )

    return {
      completed,
      total,
      percent,
      availablePoints,
      availableCoins
    }
  }, [items])

  /* Seções em "Todas", pela ordem canônica. O destaque sai do agrupamento para
     não aparecer duas vezes. Antes eram seis listas montadas à mão. */
  const groups = useMemo(() => {
    if (filter !== 'all') return []

    const featuredId = featuredQuiz?.id

    return groupByStatus(items, {
      order: QUIZ_SORT_PRIORITY,
      labels: QUIZ_STATUS_LABELS,
      statusOf: item => item.uiStatus,
      exclude: item => !!featuredId && item.id === featuredId
    })
  }, [filter, items, featuredQuiz])

  return {
    filter,
    setFilter,
    items: sortedItems,
    allItems: items,
    visibleFilters,
    groups,
    featuredQuiz,
    globalProgress,
    isEmpty: items.length === 0,
    isFilteredEmpty: sortedItems.length === 0 && items.length > 0,
    isLoading: version === ''
  }
}
